import React, { useEffect, useState, useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useApp } from '../../hooks/useApp';
import useSimulatedData from '../../hooks/useSimulatedData';
import useSimulatedAgents from '../../hooks/useSimulatedAgents';
import MegaFactoryContent from '../../components/MegaFactoryContent';
import Videoplayer from '../../components/Videoplayer';


const ProveitSim = () => {
  const { vtecAssetsUnsorted: vtecAssets, activeVideo } = useApp();
  const [activeMegaFactory, setActiveMegaFactory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vtecAssets) return;

    const megafactoryId = 'zvvIoi5j80myiEVJLVj8';
    const foundMegaFactory = vtecAssets.find(
      (asset) => asset.id === megafactoryId && asset.Type === 'megafactory'
    );

    if (foundMegaFactory) {
      setActiveMegaFactory(foundMegaFactory);
    }
    setLoading(false);
  }, [vtecAssets]);

  // Collect all topic paths from factories with data tags and agents from all suite factories
  const { topicPaths, agents } = useMemo(() => {
    if (!activeMegaFactory || !vtecAssets) return { topicPaths: [], agents: [] };
    
    // Get all factory IDs from the megafactory's suites
    const suites = activeMegaFactory.suites || [];
    const factoryIds = suites.map(s => s.factoryId).filter(Boolean);

    // Find all factories that have data tags (not just those with displayType === "media")
    const allFactories = factoryIds
      .map(id => vtecAssets.find(asset => asset.id === id))
      .filter(Boolean);

    const factoriesWithTags = allFactories.filter(
      factory => (factory?.dataTags && factory.dataTags.length > 0)
    );
    
    // Collect all topic paths from all factories with data tags
    const allTopicPaths = factoriesWithTags.flatMap(
      factory => (factory.dataTags || []).map(tag => tag.topicPath).filter(Boolean)
    );
    
    // Collect all agents from all suite factories (agents live on non-media factory asset)
    const suiteFactories = factoryIds
      .map(id => vtecAssets.find(asset => asset.id === id))
      .filter(Boolean);

    const firestoreAgents = suiteFactories.flatMap(
      factory => factory.agents || []
    );
    
    const allAgents = firestoreAgents;
    
    return { topicPaths: allTopicPaths, agents: allAgents };
  }, [activeMegaFactory, vtecAssets]);

  // Use simulated data instead of MQTT
  const { values: simulatedValues, recentlyChanged: simulatedRecentlyChanged } = useSimulatedData(topicPaths);
  
  // Use simulated agents
  const { agentStates: simulatedAgentStates } = useSimulatedAgents(agents);
  

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          flexDirection: 'column',
          background: `linear-gradient(to right, #00263e, #003b71)`,
        }}
      >
        <CircularProgress size={100} thickness={5} sx={{ color: '#F2EEA1' }} />
        <Typography fontSize={'large'} color={'#fff'} marginTop={4}>
          Loading simulation...
        </Typography>
      </Box>
    );
  }

  if (!activeMegaFactory) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          flexDirection: 'column',
          background: `linear-gradient(to right, #00263e, #003b71)`,
        }}
      >
        <Typography fontSize={'large'} color={'#fff'}>
          Megafactory not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        background: `linear-gradient(to right, #00263e, #003b71)`, 
        height: '100vh', 
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <MegaFactoryContent
        activeMegaFactory={activeMegaFactory}
        isModal={false}
        isFactoryDirect={true}
        simulatedValues={simulatedValues}
        simulatedRecentlyChanged={simulatedRecentlyChanged}
        simulatedAgentStates={simulatedAgentStates}
        simulatedAgents={agents}
        hideDiscover={true}
      />
      {activeVideo && <Videoplayer />}
    </Box>
  );
};

export default ProveitSim;
